// 2026-06-20 - FormuLearning - Step 2 §2.2 Lab Physicochemical Data (이슈 #11)
// 2026-11-03 수정 - 이슈 #112(M1016-04): Result 칸·Solid State 에 사진 0~n(LabImageSlot) + Result 문장 서술(자동 높이).
//   사진 슬롯 키 = 행의 영속 `uid`(행 index 는 삭제·삽입에 밀린다). 업로드 직전 lab_data 를 즉시 저장해 uid 를 서버에 고정한다.
// 2026-11-03 수정 - 이슈 #114: viewer 는 Lab Data 셀·노트 편집·행 추가/삭제 비활성(펼침·사진 확대는 허용).
// 파일 위치: frontend/src/features/step2/LabDataSection.tsx
// 260506 PPT 파일럿 시안 기준: Category-Method-Result 3컬럼 편집 표(행 가감) +
// Compatibility test data 표(첨가제 종류·비율·보관조건·time point) + 고체상태 파일/노트.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { deleteLabSlot, listLabImages } from '../../api/step2'
import type { LabImageMeta } from '../../api/step2'
import { apiErrorMessage } from '../../lib/apiError'
import { toast } from '../../stores/toastStore'
import LabImageSlot, { forgetLabThumb } from './LabImageSlot'
import { useCanWrite, READ_ONLY_TITLE } from '../../hooks/useCanWrite'

export interface LabRow { id: number; uid: string; category: string; method: string; result: string }
export interface CompatRow { id: number; uid: string; excipient: string; ratio: string; storage: string; timepoint: string; result: string }

export interface LabDataValues {
  physicochemical: LabRow[]
  compatibility_test: CompatRow[]
  solid_state: { dsc_file: string | null; xrd_file: string | null; notes: string }
}

// PPT 시안 기본 행 (category / method). result는 빈칸으로 시작, 사용자가 채움.
const DEFAULT_PHYSCHEM: Omit<LabRow, 'id' | 'uid'>[] = [
  { category: 'Description', method: '현미경', result: '' },
  { category: 'Particle Size', method: 'DLS', result: '' },
  { category: 'Density', method: 'Bulk', result: '' },
  { category: 'Density', method: 'Tapped', result: '' },
  { category: 'Flowability', method: 'Carr Index (or Angle of Repose or Hausner Ratio)', result: '' },
  { category: 'Solubility', method: 'MeOH', result: '' },
  { category: 'Solubility', method: 'EtOH', result: '' },
  { category: 'Solubility', method: 'ACN', result: '' },
  { category: 'Solubility', method: 'pH 1.2', result: '' },
  { category: 'Solubility', method: 'pH 2.0', result: '' },
  { category: 'Solubility', method: 'pH 3.0', result: '' },
]

const EMPTY_SOLID_STATE = { dsc_file: null, xrd_file: null, notes: '' }
const SOLID_STATE_SLOT = 'solid_state' // 백엔드 `lab_image_service.SOLID_STATE_SLOT`

function newUid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

interface Props {
  projectId: string | undefined
  labData: Record<string, unknown>
  onSave: (data: LabDataValues) => void
  /** Autosave 와 무관하게 lab_data 를 즉시 서버에 저장한다(사진 업로드 직전 uid 고정용). */
  onPersist: (data: LabDataValues) => Promise<void>
}

export default function LabDataSection({ projectId, labData, onSave, onPersist }: Props) {
  // viewer(#114) — 펼침·사진 확대 보기는 허용, 셀·노트 편집과 행 추가·삭제는 비활성.
  const canWrite = useCanWrite()
  const roTitle = canWrite ? undefined : READ_ONLY_TITLE
  // 행 식별자 카운터 (배열 index를 key로 쓰면 삭제 시 입력값이 어긋나므로 안정 id 사용).
  const uid = useRef(0)
  const nextId = () => ++uid.current

  const seedPhyschem = (): LabRow[] => {
    const rows = labData.physicochemical as Partial<Omit<LabRow, 'id'>>[] | undefined
    const src: Partial<Omit<LabRow, 'id'>>[] = Array.isArray(rows) && rows.length > 0 ? rows : DEFAULT_PHYSCHEM
    return src.map((r) => ({ id: nextId(), uid: r.uid || newUid(), category: r.category ?? '', method: r.method ?? '', result: r.result ?? '' }))
  }
  const seedCompat = (): CompatRow[] => {
    const rows = labData.compatibility_test as Partial<Omit<CompatRow, 'id'>>[] | undefined
    const src = Array.isArray(rows) ? rows : []
    return src.map((r) => ({
      id: nextId(), uid: r.uid || newUid(),
      excipient: r.excipient ?? '', ratio: r.ratio ?? '', storage: r.storage ?? '',
      timepoint: r.timepoint ?? '', result: r.result ?? '',
    }))
  }

  const [form, setForm] = useState<LabDataValues>(() => ({
    physicochemical: seedPhyschem(),
    compatibility_test: seedCompat(),
    solid_state: { ...EMPTY_SOLID_STATE, ...(labData.compatibility as Record<string, unknown> ?? labData.solid_state as Record<string, unknown> ?? {}) } as LabDataValues['solid_state'],
  }))
  const [expanded, setExpanded] = useState(Object.keys(labData).length > 0)

  const commit = (next: LabDataValues) => { setForm(next); onSave(next) }

  // ── 사진 (이슈 #112) ──
  const [images, setImages] = useState<LabImageMeta[]>([])
  const reloadImages = useCallback(() => {
    if (!projectId) return
    listLabImages(projectId).then(setImages).catch(() => {})
  }, [projectId])
  useEffect(() => { reloadImages() }, [reloadImages])
  const bySlot = useMemo(() => {
    const m = new Map<string, LabImageMeta[]>()
    for (const im of images) m.set(im.slot_key, [...(m.get(im.slot_key) ?? []), im])
    return m
  }, [images])
  // 서버에 저장된 행 uid — 저장 전 행(새 행·기본 행)에 사진을 올리면 먼저 lab_data 를 저장한다.
  const persistedUids = useRef(new Set<string>([
    ...((labData.physicochemical as { uid?: string }[] | undefined) ?? []),
    ...((labData.compatibility_test as { uid?: string }[] | undefined) ?? []),
  ].map((r) => r?.uid).filter((u): u is string => !!u)))
  const formRef = useRef(form)
  formRef.current = form
  const ensurePersisted = (slot: string) => async () => {
    if (slot === SOLID_STATE_SLOT || persistedUids.current.has(slot)) return
    const cur = formRef.current
    await onPersist(cur)
    for (const r of [...cur.physicochemical, ...cur.compatibility_test]) persistedUids.current.add(r.uid)
  }
  const slotFor = (slot: string) => (
    <LabImageSlot projectId={projectId} slot={slot} images={bySlot.get(slot) ?? []}
      onChanged={reloadImages} beforeUpload={ensurePersisted(slot)} />
  )
  /** 행 삭제 — 사진이 달린 행이면 확인 후 사진도 지운다. 취소하면 false. */
  const dropRowImages = async (uid: string): Promise<boolean> => {
    const imgs = bySlot.get(uid) ?? []
    if (!imgs.length || !projectId) return true
    if (!window.confirm(`이 행의 사진 ${imgs.length}장도 함께 삭제됩니다. 계속할까요?`)) return false
    try {
      await deleteLabSlot(projectId, uid)
      imgs.forEach((im) => forgetLabThumb(im.id))
      reloadImages()
      return true
    } catch (e) {
      toast(apiErrorMessage(e, '사진을 삭제하지 못했습니다.'), 'warn')
      return false
    }
  }

  // ── physicochemical 3컬럼 편집 ──
  const editPhys = (id: number, field: keyof Omit<LabRow, 'id'>, val: string) =>
    commit({ ...form, physicochemical: form.physicochemical.map((r) => (r.id === id ? { ...r, [field]: val } : r)) })
  const addPhys = () =>
    commit({ ...form, physicochemical: [...form.physicochemical, { id: nextId(), uid: newUid(), category: '', method: '', result: '' }] })
  const delPhys = async (row: LabRow) => {
    if (!(await dropRowImages(row.uid))) return
    const cur = formRef.current
    commit({ ...cur, physicochemical: cur.physicochemical.filter((r) => r.id !== row.id) })
  }

  // ── compatibility test data 편집 ──
  const editCompat = (id: number, field: keyof Omit<CompatRow, 'id'>, val: string) =>
    commit({ ...form, compatibility_test: form.compatibility_test.map((r) => (r.id === id ? { ...r, [field]: val } : r)) })
  const addCompat = () =>
    commit({ ...form, compatibility_test: [...form.compatibility_test, { id: nextId(), uid: newUid(), excipient: '', ratio: '', storage: '', timepoint: '', result: '' }] })
  const delCompat = async (row: CompatRow) => {
    if (!(await dropRowImages(row.uid))) return
    const cur = formRef.current
    commit({ ...cur, compatibility_test: cur.compatibility_test.filter((r) => r.id !== row.id) })
  }

  const updateNotes = (val: string) =>
    commit({ ...form, solid_state: { ...form.solid_state, notes: val } })

  const cellInput = (value: string, onChange: (v: string) => void, placeholder = '') => (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={!canWrite}
      title={roTitle}
      className="w-full rounded bg-bg-input px-2 py-1 text-[11px] text-text-body outline-none"
      placeholder={placeholder}
    />
  )
  // Result 칸 — 값뿐 아니라 문장 서술도 들어간다(회의 261016). 줄 수만큼 자동 높이.
  const resultCell = (value: string, onChange: (v: string) => void, placeholder: string, slot: string) => (
    <>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={Math.min(Math.max(value.split('\n').length, 1), 8)}
        disabled={!canWrite}
        title={roTitle}
        className="w-full resize-none rounded bg-bg-input px-2 py-1 text-[11px] text-text-body outline-none"
        placeholder={placeholder}
      />
      {slotFor(slot)}
    </>
  )

  return (
    <div className="mb-4">
      <button
        onClick={() => setExpanded(!expanded)}
        className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-text-secondary"
      >
        <span>{expanded ? '▾' : '▸'}</span>
        Lab Physicochemical Data
        <span className="text-[10px] font-normal normal-case">(optional)</span>
      </button>

      {expanded && (
        <div className="space-y-4 rounded border p-3" style={{ borderColor: 'var(--border-subtle)' }}>
          {/* 1. Physicochemical Data — Category / Method / Result 3컬럼 */}
          <div>
            <p className="mb-1.5 text-[10px] font-semibold uppercase text-text-secondary">
              1. Physicochemical Data
            </p>
            <table className="w-full text-[11px]">
              <thead>
                <tr style={{ backgroundColor: 'var(--color-bg-panel)' }}>
                  <th className="px-2 py-1 text-left font-medium text-text-secondary">Category</th>
                  <th className="px-2 py-1 text-left font-medium text-text-secondary">Method</th>
                  <th className="px-2 py-1 text-left font-medium text-text-secondary">Result</th>
                  <th className="w-6" />
                </tr>
              </thead>
              <tbody>
                {form.physicochemical.map((r) => (
                  <tr key={r.id} className="border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                    <td className="px-1 py-0.5">{cellInput(r.category, (v) => editPhys(r.id, 'category', v), 'Category')}</td>
                    <td className="px-1 py-0.5">{cellInput(r.method, (v) => editPhys(r.id, 'method', v), 'Method')}</td>
                    <td className="px-1 py-0.5">{resultCell(r.result, (v) => editPhys(r.id, 'result', v), '—', r.uid)}</td>
                    <td className="px-1 py-0.5 text-center">
                      <button onClick={() => delPhys(r)} disabled={!canWrite} className="text-[12px] text-text-muted hover:text-text-body disabled:opacity-40" title={roTitle ?? '행 삭제'}>×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button
              onClick={addPhys}
              disabled={!canWrite}
              title={roTitle}
              className="mt-1.5 rounded px-2 py-1 text-[10px] text-text-secondary transition-colors hover:bg-bg-input disabled:opacity-40"
              style={{ border: '1px solid var(--border-soft)' }}
            >
              + 항목 추가
            </button>
          </div>

          {/* 2. Compatibility Test Data — 첨가제 종류·비율·보관조건·time point */}
          <div>
            <p className="mb-1.5 text-[10px] font-semibold uppercase text-text-secondary">
              2. Compatibility Test Data
            </p>
            <table className="w-full text-[11px]">
              <thead>
                <tr style={{ backgroundColor: 'var(--color-bg-panel)' }}>
                  <th className="px-2 py-1 text-left font-medium text-text-secondary">Excipient</th>
                  <th className="px-2 py-1 text-left font-medium text-text-secondary">Ratio</th>
                  <th className="px-2 py-1 text-left font-medium text-text-secondary">Storage</th>
                  <th className="px-2 py-1 text-left font-medium text-text-secondary">Time Point</th>
                  <th className="px-2 py-1 text-left font-medium text-text-secondary">Result</th>
                  <th className="w-6" />
                </tr>
              </thead>
              <tbody>
                {form.compatibility_test.length === 0 ? (
                  <tr><td colSpan={6} className="px-2 py-2 text-[10px] text-text-muted">행을 추가해 호환성 시험 데이터를 입력하세요.</td></tr>
                ) : (
                  form.compatibility_test.map((r) => (
                    <tr key={r.id} className="border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                      <td className="px-1 py-0.5">{cellInput(r.excipient, (v) => editCompat(r.id, 'excipient', v), 'e.g. Lactose')}</td>
                      <td className="px-1 py-0.5">{cellInput(r.ratio, (v) => editCompat(r.id, 'ratio', v), 'e.g. 1:1')}</td>
                      <td className="px-1 py-0.5">{cellInput(r.storage, (v) => editCompat(r.id, 'storage', v), 'e.g. 40°C/75%RH')}</td>
                      <td className="px-1 py-0.5">{cellInput(r.timepoint, (v) => editCompat(r.id, 'timepoint', v), 'e.g. 4 weeks')}</td>
                      <td className="px-1 py-0.5">{resultCell(r.result, (v) => editCompat(r.id, 'result', v), '—', r.uid)}</td>
                      <td className="px-1 py-0.5 text-center">
                        <button onClick={() => delCompat(r)} disabled={!canWrite} className="text-[12px] text-text-muted hover:text-text-body disabled:opacity-40" title={roTitle ?? '행 삭제'}>×</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            <button
              onClick={addCompat}
              disabled={!canWrite}
              title={roTitle}
              className="mt-1.5 rounded px-2 py-1 text-[10px] text-text-secondary transition-colors hover:bg-bg-input disabled:opacity-40"
              style={{ border: '1px solid var(--border-soft)' }}
            >
              + 행 추가
            </button>
          </div>

          {/* 3. Solid State (DSC/XRD 파일 + 노트) */}
          <div>
            <p className="mb-1.5 text-[10px] font-semibold uppercase text-text-secondary">
              3. Solid State (DSC / XRD)
            </p>
            <textarea
              value={form.solid_state.notes}
              onChange={(e) => updateNotes(e.target.value)}
              rows={2}
              disabled={!canWrite}
              title={roTitle}
              className="w-full rounded bg-bg-input px-2 py-1 text-[11px] text-text-body outline-none"
              placeholder="DSC/XRD/호환성 관찰 노트..."
            />
            {slotFor(SOLID_STATE_SLOT)}
          </div>
        </div>
      )}
    </div>
  )
}
